/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import { createSelector } from 'reselect';

import {
  DAYTIME,
  EVENING,
  FAX,
  MOBILE
} from 'services/ContactMethodMaintenance';
import { mmddyyyy } from 'services/Dates';
import {
  SERVICE_TYPE_HNW,
  SERVICE_TYPE_MIL,
  SERVICE_TYPE_CORE
} from 'services/ServiceBenefits';
import {
  relationshipManagerImageUrl,
  relationshipManagerProfileUrl
} from 'services/Externals';
import { getRouter } from 'state/router/RouterSelectors';
import { getRMDData } from 'state/distribution/RequiredMinimumDistributionSelectors';
import { residentialAddressEnabled } from 'services/FeatureFlags';

export const getCustomer = (state) => state.customer;
export const getMailingAddress = (state) => state.mailingAddress;
export const getResidentialAddressV2 = (state) => state.residentialAddress;
export const getNotifications = (state) => state.notification;

export const getCustomerResidentialState = createSelector(
  getMailingAddress,
  getResidentialAddressV2,
  (mailingAddress, residentialAddress) => {
    if(residentialAddressEnabled()) {
      return residentialAddress && residentialAddress.state ? residentialAddress.state : null;
    } else {
      return mailingAddress && mailingAddress.state ? mailingAddress.state : null;
    }
  }
);

export const getLdapId = createSelector(
  getCustomer,
  (customer) => customer.ldapId
);
export const getCustomerAge = createSelector(
  getCustomer,
  (customer) => customer.age
);

export const getCustomerServiceType = createSelector(getCustomer, (customer) =>
  customer ? customer.serviceType : null
);

export const getCustomerServiceLevel = createSelector(getCustomer, (customer) =>
  customer ? customer.serviceLevel : null
);

export const isEpsOrPsClient = createSelector(
  getCustomerServiceType,
  (serviceType) =>
    serviceType === SERVICE_TYPE_MIL || serviceType === SERVICE_TYPE_HNW
);

export const hasServiceLevel = createSelector(
  getCustomerServiceType,
  (serviceType) =>
    serviceType === SERVICE_TYPE_MIL ||
    serviceType === SERVICE_TYPE_HNW ||
    serviceType === SERVICE_TYPE_CORE
);

export const getCustomerWebView = createSelector(getCustomer, (customer) =>
  Boolean(customer.webViewEnabled)
);

export const getCustomerName = createSelector(
  getCustomer,
  (customer) => customer.fullName || ''
);

export const getUberId = createSelector(
  getCustomer,
  (customer) => customer.uberId || ''
);

export const getDelegatedUserId = createSelector(
  getCustomer,
  (customer) => customer.delegatedUserId || ''
);

export const getCustomerBusinessPartyId = createSelector(
  getCustomer,
  (customer) => customer.businessPartyId
);

export const getCustomerTaxId = createSelector(
  getCustomer,
  (customer) => customer.taxId
);

export const getCustomerBirthday = createSelector(getCustomer, (customer) =>
  mmddyyyy(customer.birthDate)
);

export const isAdvicePlanningClient = createSelector(
  getCustomer,
  (customer) => customer && customer.isAdvicePlanningClient
);

export const isAdviceConsentExpired = createSelector(
  getCustomer,
  (customer) => customer && customer.isAdviceConsentExpired
);

export const isRetirementAdvisoryServiceClient = createSelector(
  getCustomer,
  (customer) => customer && customer.isRetirementAdvisoryServiceClient
);

export const isAdvicePlanningOnboardingEngagementCompleted = createSelector(
  getCustomer,
  (customer) =>
    customer && customer.isAdvicePlanningOnboardingEngagementCompleted
);

export const isRMDEligible = createSelector(
  getRMDData,
  (requiredMinimumDistribution) =>
    requiredMinimumDistribution && requiredMinimumDistribution.customerEligible
);

export const getCustomerHouseholdId = createSelector(
  getCustomer,
  (customer) =>
    customer &&
    customer.planningSummary &&
    customer.planningSummary.planningHouseholdId
);

export const getCustomerPlanningGroupId = createSelector(
  getCustomer,
  (customer) =>
    customer &&
    customer.planningSummary &&
    customer.planningSummary.planningGroupId
);

export const isActiveHousehold = createSelector(
  getCustomer,
  (customer) =>
    customer &&
    customer.planningSummary &&
    customer.planningSummary.activeHousehold
);

export const getDocumentDate = createSelector(
  getCustomer,
  (customer) =>
    customer &&
    customer.planningSummary &&
    customer.planningSummary.documentDate
);

export const getModelName = createSelector(
  getCustomer,
  (customer) =>
    customer && customer.planningSummary && customer.planningSummary.modelName
);

export const getPreferredPortfolioName = createSelector(
  getCustomer,
  (customer) =>
    customer &&
    customer.planningSummary &&
    customer.planningSummary.preferredPortfolioName
);

export const getPlanningSummaryClientUberId = createSelector(
  getCustomer,
  (customer) =>
    (customer &&
      customer.planningSummary &&
      customer.planningSummary.clientUberId) ||
    null
);

export const getPlanningSummaryCoClientUberId = createSelector(
  getCustomer,
  (customer) =>
    (customer &&
      customer.planningSummary &&
      customer.planningSummary.coClientUberId) ||
    null
);

export const getCustomerProductTypes = (state) => state.clientProductTypes;

export const getCustomerProductTypeIds = createSelector(
  getCustomerProductTypes,
  (productTypes) => Object.keys(productTypes).map((id) => parseInt(id))
);

const getCustomerPreferences = (state) => state.customerPreferences;

export const getCustomerContactInformation = createSelector(
  getCustomerPreferences,
  (customerPreferences) => customerPreferences.contactInformation || {}
);

export const getCustomerTrustedContacts = createSelector(
  getCustomerPreferences,
  (customerPreferences) => customerPreferences.trustedContacts || []
);

export const getOrderedCustomerTrustedContacts = createSelector(
  getCustomerTrustedContacts,
  (customerPreferences) =>
    customerPreferences.sort((a, b) => a.sequenceNumber - b.sequenceNumber)
);

export const hasResidentialAddress = createSelector(
  getCustomerContactInformation,
  (contactInformation) =>
    contactInformation &&
    contactInformation.residentialAddress &&
    contactInformation.residentialAddress.addressLines ? true : false
);

export const getPrimaryMailingAddress = createSelector(
  getCustomerContactInformation,
  (contactInformation) => {
    if (contactInformation && contactInformation.primaryMailingAddress) {
      return contactInformation.primaryMailingAddress;
    }
    return {};
  }
);

export const getResidentialAddress = createSelector(
  getCustomerContactInformation,
  (contactInformation) => {
    if (contactInformation && contactInformation.residentialAddress) {
      return contactInformation.residentialAddress;
    }
    return {};
  }
);

export const getIsMailingAddressSameAsResidential = createSelector(
  hasResidentialAddress,
  getPrimaryMailingAddress,
  (hasResidential, primaryMailingAddress) =>
    hasResidential &&
    primaryMailingAddress &&
    primaryMailingAddress.sameAsResidential
);

export const getPrimaryEmailAddress = createSelector(
  getCustomerContactInformation,
  (contactInformation) => contactInformation.primaryEmailAddress || {}
);

/* This will only return the string if you need the full object with contact id use getPrimaryEmailAddress */
export const getPrimaryEmail = createSelector(
  getPrimaryEmailAddress,
  (primaryEmailAddress) => {
    return (primaryEmailAddress && primaryEmailAddress.address) || '';
  }
);

export const getPhoneNumbers = createSelector(
  getCustomerContactInformation,
  (contactInformation) => contactInformation.phoneNumbers || []
);

export const getDaytimePhoneNumber = createSelector(
  getPhoneNumbers,
  (phoneNumbers) =>
    phoneNumbers.find((phoneNumber) => phoneNumber.typeId === DAYTIME)
);

export const getEveningPhoneNumber = createSelector(
  getPhoneNumbers,
  (phoneNumbers) =>
    phoneNumbers.find((phoneNumber) => phoneNumber.typeId === EVENING)
);

export const getMobilePhoneNumber = createSelector(
  getPhoneNumbers,
  (phoneNumbers) =>
    phoneNumbers.find((phoneNumber) => phoneNumber.typeId === MOBILE)
);

export const getFaxNumber = createSelector(getPhoneNumbers, (phoneNumbers) =>
  phoneNumbers.find((phoneNumber) => phoneNumber.typeId === FAX)
);

const getPreferencesSubscriptions = createSelector(
  getCustomerPreferences,
  (customerPreferences) => customerPreferences.subscriptions || {}
);

export const getPreferencesComplianceSubscriptions = createSelector(
  getPreferencesSubscriptions,
  (subscriptions) => subscriptions.complianceSubscriptions || []
);

const breadcrumbLabelMap = {
  'contact-information': 'Contact Information',
  'trusted-contacts': 'Trusted Contacts',
  beneficiaries: 'Beneficiary on File',
  checkwriting: 'Checkwriting',
  'customize-account-view': 'Customize Account View',
  profile: 'Profile',
  'cost-basis': 'Cost Basis',
  'dividends-and-capital-gains': 'Dividends & Capital Gains Distributions',
  'document-delivery': 'Document Delivery'
};

export const getPreferencesBreadcrumbs = createSelector(getRouter, (router) => {
  const { location } = router;
  const { pathname } = location;
  const pathnames = pathname.replace('/', '').split('/');
  const links = [
    {
      label: 'My Accounts',
      url: '/'
    }
  ];
  let base = '';
  pathnames.forEach((path) => {
    base = `${base}/${path}`;
    links.push({
      label: breadcrumbLabelMap[path],
      url: base
    });
  });

  return links;
});

export const getUnreadMessageCount = createSelector(
  getNotifications,
  (notifications) => {
    if (notifications && notifications.unreadMessageCount > 0) {
      return notifications.unreadMessageCount;
    }
    return 0;
  }
);

/*
 {
 "relationshipManager": {
 "firstName": "john",
 "lastName": "doe",
 "displayName": "john doe",
 "email": "john.doe@troweprice.com",
 "officePhone": "123-345-5555",
 "officeLocation": "Owings Mills, MD",
 "jobTitle": "Relationship Manager",
 "imageUrl": "/content/dam/trp-library/headshots/john_doe_250x350.jpg"
 }
 }
 */

export const getCustomerForNavigation = createSelector(
  getCustomer,
  getUnreadMessageCount,
    getRMDData,
  (customer, unreadMessagesCount, rmdData) => {
    const {
      customerEligible: isRMDEligible,
      customerPreEligible: isRMDPreEligible
    } = rmdData;
    const {
      relationshipManager,
      isAdvicePlanningClient = false,
      isAdviceConsentExpired = false,
      isRetirementAdvisoryServiceClient = false
    } = customer;
    if (relationshipManager) {
      const {
        firstName,
        lastName,
        displayName,
        email,
        officePhone,
        officeLocation,
        jobTitle,
        imageUrl
      } = relationshipManager;
      const profile = relationshipManagerProfileUrl(firstName, lastName);
      const headshot = relationshipManagerImageUrl(imageUrl);
      const person = {
        firstName,
        lastName,
        displayName
      };
      const manager = {
        person,
        title: jobTitle,
        profile,
        headshot,
        location: officeLocation,
        phone: officePhone,
        email
      };
      return {
        ...customer,
        messageCount: unreadMessagesCount,
        isAdvicePlanningClient:
          isAdvicePlanningClient && !isAdviceConsentExpired,
        isRetirementAdvisoryServiceClient,
        relationshipManager: {
          ...manager
        },
        isRMDEligible,
        isRMDPreEligible
      };
    }
    return {
      ...customer,
      messageCount: unreadMessagesCount,
      isAdvicePlanningClient: isAdvicePlanningClient && !isAdviceConsentExpired,
      isRetirementAdvisoryServiceClient,
      isRMDEligible,
      isRMDPreEligible
    };
  }
);
